import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles, Paper, Input, Button, IconButton, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import type { Deal } from '@paintscout/api'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

const PROFIT_GREEN = '#159948'

const useStyles = makeStyles<Theme, { cardDisplay?: boolean }>(
  (theme) => ({
    root: ({ cardDisplay }) => (cardDisplay ? { boxShadow: 'none', border: 'none', paddingTop: theme.spacing(2) } : {}),
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(0.5)
    },
    title: {
      margin: 0,
      paddingTop: theme.spacing(0.5)
    },
    content: {
      paddingTop: 0,
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5)
    },
    inputSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${theme.palette.grey[200]}`
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    label: {
      color: theme.palette.grey[700]
    },
    percentHint: {
      color: theme.palette.grey[500]
    },
    inputRight: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    currencySymbol: {
      color: theme.palette.grey[500]
    },
    numberInput: {
      width: 96
    },
    numberInputField: {
      textAlign: 'right',
      '&::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      },
      '&::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      }
    },
    summarySection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      paddingTop: theme.spacing(0.5)
    },
    summaryAndGraph: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2)
    },
    summaryRows: {
      flex: 1,
      minWidth: 160,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5)
    },
    summaryValue: {
      fontWeight: 600,
      color: theme.palette.text.primary
    },
    dividerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    profitPositive: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: PROFIT_GREEN
    },
    profitNegative: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: theme.palette.error.main
    },
    incomplete: {
      color: theme.palette.grey[500],
      fontStyle: 'italic'
    },
    graphWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    graphLabel: {
      fontSize: '0.75rem',
      color: theme.palette.grey[600],
      marginBottom: theme.spacing(1)
    },
    graphContainer: {
      position: 'relative',
      width: 96,
      height: 96
    },
    graphCenter: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      paddingTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.grey[200]}`
    }
  }),
  { name: 'JobCostingCard' }
)

export interface JobCostingCardProps {
  deal: Deal
  onSave: (deal: Deal) => Promise<void>
  cardDisplay?: boolean
}

function getProfitColor(profitMargin: number, errorColor: string): string {
  if (profitMargin < 5) return errorColor
  if (profitMargin < 20) return '#F5A623'
  return PROFIT_GREEN
}

function getMarginFontSize(absMargin: number): string {
  if (absMargin >= 100000) return '0.625rem'
  if (absMargin >= 10000) return '0.75rem'
  if (absMargin >= 1000) return '0.875rem'
  if (absMargin >= 100) return '1.125rem'
  return '1.25rem'
}

function getPercentFontSize(absMargin: number): string {
  if (absMargin >= 100000) return '0.5rem'
  if (absMargin >= 10000) return '0.625rem'
  if (absMargin >= 1000) return '0.75rem'
  return '0.875rem'
}

function handleInputChange(value: string, setter: (value: string) => void) {
  if (value === '') {
    setter(value)
    return
  }
  const cleanedValue = value.replace(/^0+(?=\d)/, '')
  const numValue = parseFloat(cleanedValue)
  if (!isNaN(numValue) && numValue >= 0 && numValue <= 99999999) {
    setter(cleanedValue)
  }
}

const toStr = (v?: number | null) => (v != null ? String(v) : '')
const toNum = (s: string) => (s !== '' ? parseFloat(s) : undefined)

function JobCostingCard({ deal, onSave, cardDisplay }: JobCostingCardProps) {
  const classes = useStyles({ cardDisplay })
  const [labour, setLabour] = useState(toStr(deal?.jobCosting?.labour))
  const [materials, setMaterials] = useState(toStr(deal?.jobCosting?.materials))
  const [overhead, setOverhead] = useState(toStr(deal?.jobCosting?.overhead))
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [saving, setSaving] = useState(false)
  const dealRef = useRef(deal)
  dealRef.current = deal

  // Sync input values when saved deal comes back (new _rev from server).
  // Use functional setState so React skips the re-render when the value hasn't changed.
  useEffect(() => {
    const newLabour = toStr(deal?.jobCosting?.labour)
    const newMaterials = toStr(deal?.jobCosting?.materials)
    const newOverhead = toStr(deal?.jobCosting?.overhead)
    setLabour((prev) => (prev === newLabour ? prev : newLabour))
    setMaterials((prev) => (prev === newMaterials ? prev : newMaterials))
    setOverhead((prev) => (prev === newOverhead ? prev : newOverhead))
  }, [deal?._rev])

  const isDirty =
    labour !== toStr(deal?.jobCosting?.labour) ||
    materials !== toStr(deal?.jobCosting?.materials) ||
    overhead !== toStr(deal?.jobCosting?.overhead)

  const jobTotal = deal?.value ?? 0
  const costTotal = parseFloat(labour || '0') + parseFloat(materials || '0') + parseFloat(overhead || '0')
  const difference = jobTotal - costTotal
  const profitMargin = jobTotal > 0 ? (difference / jobTotal) * 100 : 0
  const isConfigured = costTotal > 0

  // Use largest-remainder rounding so the three displayed percentages always sum to exactly 100%.
  // Independently rounding each (e.g. via toFixed) can produce totals of 99% or 101%.
  // Memoized so the percent values are referentially stable across re-renders (e.g. the
  // re-render triggered by the new _rev coming back from save) — otherwise recomputing
  // this inline on every render caused a visible flash on save.
  const { labourPercent, materialsPercent, overheadPercent } = useMemo(() => {
    if (costTotal <= 0) return { labourPercent: 0, materialsPercent: 0, overheadPercent: 0 }
    const raw = {
      labour: (parseFloat(labour || '0') / costTotal) * 100,
      materials: (parseFloat(materials || '0') / costTotal) * 100,
      overhead: (parseFloat(overhead || '0') / costTotal) * 100
    }
    const result = {
      labour: Math.floor(raw.labour),
      materials: Math.floor(raw.materials),
      overhead: Math.floor(raw.overhead)
    }
    let remainder = 100 - (result.labour + result.materials + result.overhead)
    const byFraction = (['labour', 'materials', 'overhead'] as const)
      .map((k) => ({ k, frac: raw[k] - Math.floor(raw[k]) }))
      .sort((a, b) => b.frac - a.frac)
    for (const { k } of byFraction) {
      if (remainder <= 0) break
      result[k] += 1
      remainder -= 1
    }
    return {
      labourPercent: result.labour,
      materialsPercent: result.materials,
      overheadPercent: result.overhead
    }
  }, [labour, materials, overhead, costTotal])

  const absMargin = Math.abs(profitMargin)
  const profitColor = getProfitColor(profitMargin, '#CD2929')

  // Keep a stable ref to onSave so handleSave doesn't depend on the prop identity
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await onSaveRef.current({
        ...dealRef.current,
        jobCosting: {
          labour: toNum(labour),
          materials: toNum(materials),
          overhead: toNum(overhead)
        }
      })
    } finally {
      setSaving(false)
    }
  }, [labour, materials, overhead])

  const handleReset = () => {
    setLabour(toStr(deal?.jobCosting?.labour))
    setMaterials(toStr(deal?.jobCosting?.materials))
    setOverhead(toStr(deal?.jobCosting?.overhead))
  }

  const sharedInputProps = useMemo(
    () => ({
      type: 'number' as const,
      classes: { root: classes.numberInput, input: classes.numberInputField },
      onWheel: (e: React.WheelEvent<HTMLDivElement>) => (e.target as HTMLInputElement).blur(),
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
          e.preventDefault()
          ;(e.target as HTMLInputElement).select()
        }
      }
    }),
    [classes.numberInput, classes.numberInputField]
  )

  // Clamp to [0, 100] so negative margins don't produce an inverted arc
  const clampedMargin = Math.max(0, Math.min(profitMargin, 100))

  return (
    <Paper className={classes.root}>
      {!cardDisplay && (
        <div className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            Job Costing
          </Typography>
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand job costing' : 'Collapse job costing'}
          >
            {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </div>
      )}

      <div className={classes.content}>
        {isCollapsed ? (
          <div className={classes.row}>
            <Typography variant="body2" className={classes.label}>
              Profit
            </Typography>
            {isConfigured ? (
              <Typography variant="body2" style={{ fontWeight: 600, fontSize: '1.125rem', color: profitColor }}>
                {difference >= 0 ? '+' : ''}${difference.toLocaleString()} ({profitMargin.toFixed(1)}%)
              </Typography>
            ) : (
              <Typography variant="body2" className={classes.incomplete}>
                Incomplete
              </Typography>
            )}
          </div>
        ) : (
          <>
            {/* Cost inputs */}
            <div className={classes.inputSection}>
              <div className={classes.row}>
                <Typography variant="body2" component="label" className={classes.label} htmlFor="job-costing-labour">
                  Labor
                  {isConfigured && parseFloat(labour || '0') > 0 && (
                    <Typography variant="body2" component="span" className={classes.percentHint}>
                      {' '}
                      ({labourPercent.toFixed(0)}%)
                    </Typography>
                  )}
                </Typography>
                <div className={classes.inputRight}>
                  <Typography variant="body2" className={classes.currencySymbol}>
                    $
                  </Typography>
                  <Input
                    {...sharedInputProps}
                    id="job-costing-labour"
                    value={labour}
                    onChange={(e) => handleInputChange(e.target.value, setLabour)}
                  />
                </div>
              </div>

              <div className={classes.row}>
                <Typography variant="body2" component="label" className={classes.label} htmlFor="job-costing-materials">
                  Materials
                  {isConfigured && parseFloat(materials || '0') > 0 && (
                    <Typography variant="body2" component="span" className={classes.percentHint}>
                      {' '}
                      ({materialsPercent.toFixed(0)}%)
                    </Typography>
                  )}
                </Typography>
                <div className={classes.inputRight}>
                  <Typography variant="body2" className={classes.currencySymbol}>
                    $
                  </Typography>
                  <Input
                    {...sharedInputProps}
                    id="job-costing-materials"
                    value={materials}
                    onChange={(e) => handleInputChange(e.target.value, setMaterials)}
                  />
                </div>
              </div>

              <div className={classes.row}>
                <Typography variant="body2" component="label" className={classes.label} htmlFor="job-costing-overhead">
                  Other
                  {isConfigured && parseFloat(overhead || '0') > 0 && (
                    <Typography variant="body2" component="span" className={classes.percentHint}>
                      {' '}
                      ({overheadPercent.toFixed(0)}%)
                    </Typography>
                  )}
                </Typography>
                <div className={classes.inputRight}>
                  <Typography variant="body2" className={classes.currencySymbol}>
                    $
                  </Typography>
                  <Input
                    {...sharedInputProps}
                    id="job-costing-overhead"
                    value={overhead}
                    onChange={(e) => handleInputChange(e.target.value, setOverhead)}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className={classes.summarySection}>
              <div className={isConfigured ? classes.summaryAndGraph : undefined}>
                <div className={classes.summaryRows}>
                  <div className={classes.row}>
                    <Typography variant="body2" className={classes.label}>
                      Job Value
                    </Typography>
                    <Typography variant="body2" className={classes.summaryValue}>
                      ${jobTotal.toLocaleString()}
                    </Typography>
                  </div>
                  <div className={classes.row}>
                    <Typography variant="body2" className={classes.label}>
                      Total Costs
                    </Typography>
                    {isConfigured ? (
                      <Typography variant="body2" className={classes.summaryValue}>
                        ${costTotal.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" style={{ fontWeight: 600, color: '#ADB5BD' }}>
                        —
                      </Typography>
                    )}
                  </div>
                  <div className={classes.dividerRow}>
                    <Typography variant="body2" className={classes.label}>
                      Profit
                    </Typography>
                    {isConfigured ? (
                      <Typography variant="body2" style={{ fontWeight: 600, fontSize: '1.125rem', color: profitColor }}>
                        {difference >= 0 ? '+' : ''}${difference.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" className={classes.incomplete}>
                        Incomplete
                      </Typography>
                    )}
                  </div>
                </div>

                {/* Profit margin donut — only shown when costs are configured */}
                {isConfigured && (
                  <div className={classes.graphWrapper}>
                    <Typography variant="caption" className={classes.graphLabel}>
                      Profit Margin
                    </Typography>
                    <div className={classes.graphContainer}>
                      <svg style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }} viewBox="0 0 120 120">
                        <circle cx="60" cy="60" r="50" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                        <circle
                          cx="60"
                          cy="60"
                          r="50"
                          fill="none"
                          stroke={profitColor}
                          strokeWidth="10"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 50}`}
                          strokeDashoffset={`${2 * Math.PI * 50 * (1 - clampedMargin / 100)}`}
                          style={{ transition: 'stroke-dashoffset 0.3s' }}
                        />
                      </svg>
                      <div className={classes.graphCenter}>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, color: profitColor, fontSize: getMarginFontSize(absMargin) }}
                        >
                          {Math.round(profitMargin)}
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, color: profitColor, fontSize: getPercentFontSize(absMargin) }}
                        >
                          %
                        </Typography>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dirty state actions */}
            {isDirty && (
              <div className={classes.actions}>
                <Button variant="text" onClick={handleReset} disabled={saving}>
                  Reset
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  )
}

export default React.memo(JobCostingCard, (prev, next) => {
  // Only re-render when deal identity/rev or cardDisplay actually changes.
  // onSave is captured via ref so its identity doesn't matter.
  return (
    prev.deal?._rev === next.deal?._rev &&
    prev.deal?.value === next.deal?.value &&
    prev.cardDisplay === next.cardDisplay
  )
})
